<template>
  <v-navigation-drawer v-model="drawer" @input="handleDrawerToggle" fixed temporary right width="350px">
    <v-container v-if="companiesRequest.loading">
      <v-row justify="center">
        <v-progress-circular :size="50" :width="5" color="primary" indeterminate />
      </v-row>
    </v-container>
    <v-banner sticky class="mt-0 mx-0 py-1 primary" tile v-if="!companiesRequest.loading">
      <v-btn text icon color="white" @click="toggleDrawer">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-container class="ma-0 px-0">
        <v-row class="pa-0">
          <v-col cols="12" class="py-0">
            <v-text-field
              v-lazy-input:debounce="1200"
              v-model="search"
              :label="$lang('main.settings.search_property')"
              dark
              flat
              solo-inverted
              clearable
              prepend-inner-icon="mdi-magnify"
              hide-details
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="userClusters.length" cols="12">
            <v-select
              v-model="selectedClusters"
              :items="userClusters"
              :label="$lang('main.titles.user_property_clusters')"
              item-value="id"
              multiple
              dense
              solo-inverted
              dark
              flat
              return-object
              hide-details
              :menu-props="{
                closeOnContentClick: true,
                closeOnClick: true
              }"
            >
              <template v-slot:item="{ item }">
                <v-chip small label :color="item.color" class="white--text">
                  <v-icon class="mr-1" small>{{ item.icon }}</v-icon>
                  {{ item.name }}
                </v-chip>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip small label :color="item.color" class="white--text ma-1">
                  <v-icon class="mr-1" small>{{ item.icon }}</v-icon>
                  {{ item.name }}
                </v-chip>
              </template>
              <template v-slot:append>
                <v-chip v-if="extraSelectionCount && selectedClusters.length" small label class="white--text ma-1">
                  <v-icon class="mr-1" small>mdi-plus-box-multiple</v-icon>
                  {{ extraSelectionCount }}
                </v-chip>
              </template>
              <template v-slot:append-item>
                <v-list-item :to="{ name: 'user-property-clusters' }">
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon>mdi-home-group</v-icon>
                      {{ $lang('main.titles.user_property_clusters') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row class="pa-0" v-if="$can('INTERNAL_SUPPORT')">
          <v-col xs="12" class="pb-0 mb-0 admin">
            <c-companies-autocomplete
              dense
              v-model="filters.parent_id"
              clearable
              label="Filtro aziende e figlie"
            />
          </v-col>
        </v-row>
        <v-row class="pa-0">
          <v-col xs="12" class="pb-0 mb-0">
            <v-btn @click="clearSelections" x-small block>{{ $lang('main.settings.deselect_all') }}</v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-banner>
    <v-card-text class="px-0" v-if="!companiesRequest.loading">
      <v-row class="px-2 pt-0">
        <v-col cols="12" align="center" class="pt-0">
          <v-chip class="ma-2" small>
            <span>{{ selected.length }} {{ selected.length > 1 ? $lang('main.settings.properties_selected') : $lang('main.settings.property_selected') }}</span>
          </v-chip>
          <v-btn @click="fetchCompaniesData" icon color="green">
            <v-icon>mdi-cached</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-treeview
        v-model="selected"
        :items="companies"
        :search="search"
        :filter="filter"
        selectable
        selection-type="leaf"
        dense
        hoverable
        open-on-click
        selected-color="accent"
      >
        <template v-slot:append="{ item }">
          <v-icon v-if="!item.icon && item.children">mdi-briefcase</v-icon>
          <v-icon v-if="!item.children">mdi-office-building</v-icon>
        </template>
        <template v-slot:label="{ item }">
          <v-chip v-if="item.color" small :color="item.color" label class="white--text">
            <v-icon small class="mr-1">{{ item.icon }}</v-icon>
            {{ item.name }}
          </v-chip>
          <span v-else>{{ item.name }}</span>
        </template>
      </v-treeview>
      <v-fab-transition>
        <v-btn color="success" dark fixed bottom right fab @click="toggleDrawer">
          <v-icon>mdi-check</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-card-text>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";
import MixpanelEvents from "@/constants/events";
import {
  STORE_PROPERTY_IDS,
  RETRIEVE_PROPERTY_IDS,
  GET_SETTINGS_COMPANIES_COLLECTION,
} from "@/store/settings/actions";

export default {
  name: "Settings",
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      drawer: this.isOpen,
      selected: [],
      search: '',
      caseSensitive: false,
      selectedClusters: [],
      filters: {
        parent_id: null
      },
      searchTimeout: null,
      searchInputHandler: null,
    };
  },
  beforeMount() {
    this.fetchCompaniesData();
    this.retrieveSelectedPropertyIds();
  },
  mounted() {
    // Add event listeners to improve treeview performance
    this.addTreeviewOptimizations();
  },
  beforeDestroy() {
    // Clean up event listeners
    this.removeTreeviewOptimizations();
  },
  methods: {
    fetchCompaniesData() {
      this.$store.dispatch(GET_SETTINGS_COMPANIES_COLLECTION, {
        settings: {
          ...this.filters
        }
      });
    },
    retrieveSelectedPropertyIds() {
      this.$store.dispatch(RETRIEVE_PROPERTY_IDS).then(() => {
        this.selected = this.propertyIds;
      });
    },
    clearSelections() {
      this.selected = [];
      this.selectedClusters = [];
    },
    applyChanges() {
      let type = 'manual';

      if (this.selectedClusters.length) {
        type = 'cluster';
      }

      this.$monitorService.track(MixpanelEvents.PROPERTIES_FILTERED, {
        property_count: this.selected.length,
        filter_type: type,
      });
      
      this.$store.dispatch(STORE_PROPERTY_IDS, this.selected);
    },
    handleDrawerToggle(state) {
      this.applyChanges();
      if (!state) {
        this.$emit('close-settings');
      }
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    optimizeSearch() {
      if (this.search && this.search.length > 0) {
        if (this.search.length < 3 && this.isSearchPerformanceIssue()) {
          this.search = '';
          this.$notify({
            type: 'warning',
            text: this.$lang('main.settings.search_performance_warning', 'Please use a more specific search term (at least 3 characters) for better performance.')
          });
          return;
        }
      }
    },
    isSearchPerformanceIssue() {
      const totalItems = this.getTotalItemCount();
      return totalItems > 2000;
    },
    getTotalItemCount() {
      if (!this.companiesRequest.data.collection) return 0;
      
      let count = 0;
      this.companiesRequest.data.collection.forEach(company => {
        company.property_clusters.forEach(cluster => {
          count += cluster.properties.length || 0;
        });
      });
      
      return count;
    },
    addTreeviewOptimizations() {
      // Add a small delay after search input to prevent UI freezing
      this.searchInputHandler = () => {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }
        
        // Use requestAnimationFrame to avoid blocking the main thread
        this.searchTimeout = setTimeout(() => {
          window.requestAnimationFrame(() => {
            // This empty callback ensures the UI updates before heavy operations
          });
        }, 50);
      };
      
      // Find the search input element and add our optimization
      this.$nextTick(() => {
        const searchInput = this.$el.querySelector('input[type="text"]');
        if (searchInput) {
          searchInput.addEventListener('input', this.searchInputHandler);
        }
      });
    },
    
    removeTreeviewOptimizations() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      const searchInput = this.$el.querySelector('input[type="text"]');
      if (searchInput && this.searchInputHandler) {
        searchInput.removeEventListener('input', this.searchInputHandler);
      }
    }
  },
  computed: {
    companies() {
      let propertyIds = new Set();
      if (!this.companiesRequest.data.collection) return [];

      const result = this.companiesRequest.data.collection.reduce((companies, company) => {
        if (!company.property_clusters.length) return companies;

        const clusters = company.property_clusters
          .filter(cluster => cluster.properties.length)
          .map(cluster => {
            const properties = cluster.properties.map(property => {
              propertyIds.add(property.id);
              return {
                id: property.id,
                name: property.name,
              };
            });

            return {
              id: `cl_${cluster.id}`,
              name: cluster.name,
              icon: cluster.icon,
              color: cluster.color,
              children: properties,
            };
          });

        if (clusters.length) {
          companies.push({
            id: `comp_${company.id}`,
            name: company.name,
            children: clusters.length === 1 ? clusters[0].children : clusters,
          });
        }

        return companies;
      }, []);

      this.$monitorService.setProfileProperties({
        'Managed Properties': propertyIds.size,
      });      

      return result;
    },
    userClusters() {
      return this.userData.clusters?.map(cluster => ({
        id: cluster.id,
        name: cluster.name,
        icon: cluster.icon,
        color: cluster.color,
        propertyIds: cluster.properties.map(property => property.id)
      })) || [];
    },
    filter() {
      if (this.search && this.search.length > 0) {
        // Pre-process the search term for better performance
        const searchTerm = this.caseSensitive ? this.search : this.search.toLowerCase();
        
        return (item, search, textKey) => {
          // Skip filtering if the search term is too short (unless it's an exact match)
          if (searchTerm.length < 3 && item[textKey] !== searchTerm) {
            return true; // Show all items if search term is too short
          }
          
          // Use the most efficient string comparison method
          if (!this.caseSensitive) {
            return item[textKey].toLowerCase().includes(searchTerm);
          }
          return item[textKey].includes(searchTerm);
        };
      }
      return undefined;
    },
    extraSelectionCount() {
      const clusterPropertyIds = this.selectedClusters.flatMap(cluster => cluster.propertyIds);
      return Math.max(0, this.selected.length - clusterPropertyIds.length);
    },
    ...mapState({
      propertyIds: state => state.settings.property.ids,
      companiesRequest: state => state.settings.getCompaniesRequest,
    })
  },
  watch: {
    isOpen(val) {
      this.drawer = val;
    },
    selectedClusters(val) {
      this.selected = val.flatMap(cluster => cluster.propertyIds);
    },
    'filters': {
      handler() {
        this.fetchCompaniesData();
      },
      deep: true,
    },
    search(val) {
      if (val && val.length > 0) {
        this.optimizeSearch();
      }
    }
  }
};
</script>
